import React, { createContext, Dispatch, SetStateAction, useEffect, useState } from "react";
import { User } from "../components/types";
import { UserContextProviderProps } from "./types";

export const UserStateContext = createContext<User>(undefined);
UserStateContext.displayName = "UserStateContext";

export const UserStateSetterContext = createContext<Dispatch<SetStateAction<User>>>(undefined);
UserStateSetterContext.displayName = "UserStateSetterContext";

/**
 * A context provider that provides a global "user" state for its children sub-tree. It allows conditionally rendering
 * UI elements and executing application logic based on this global user state.
 *
 * It accepts a user object as prop. It holds this object as state. It renders its `children` wrapped in context
 * providers. These context providers make this state and the associated state setter function available to any
 * component rendered within their children sub-tree. These can be accessed using the following companion hooks.
 *
 * ### `useUser`
 *
 * This hook returns the current user state value from the context provider.
 *
 * ### `useSetUser`
 *
 * This hook returns a state setter function that can be used to update the user state value in the context provider.
 * When this state is updated, all component within this provider that use this state will be re-rendered using the
 * updated state value.
 */
export const UserContextProvider = <U extends User = User>({
  user: userProp,
  children,
}: UserContextProviderProps<U>) => {
  const [user, setUser] = useState(userProp);

  useEffect(() => {
    setUser((userState) => (userProp !== userState ? userProp : userState));
  }, [userProp]);

  return (
    <UserStateContext.Provider value={user}>
      <UserStateSetterContext.Provider value={setUser}>{children}</UserStateSetterContext.Provider>
    </UserStateContext.Provider>
  );
};
